// frontend/src/pages/ImageTools/ImageTools.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ToolCard from '../../components/ToolCard/ToolCard';
import api from '../../services/api';

const ImageTools = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // We'll store only the data for the "Image tools" category
  const [imageCategoryData, setImageCategoryData] = useState(null); 
  const [loading, setLoading] = useState(true);
  
  // Get the current language code (e.g., 'en', 'de', 'fr')
  const lang = i18n.language.split('-')[0];

  useEffect(() => {
    const fetchToolsData = async () => {
      try {
        // We still fetch all tools, as the endpoint provides them grouped
        const response = await api.get('/tools/'); 
        
        // **KEY CHANGE**: Instead of storing all categories,
        // we find and store only the "Text tools" data.
        if (response.data['Image tools']) {
          setImageCategoryData(response.data['Image tools']);
        } else {
          console.warn('"Image tools" category not found in API response.');
          setImageCategoryData({ tools: [] }); // Set to empty to avoid errors
        }
        
      } catch (error) {
        console.error('Error fetching image tools:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchToolsData();
  }, []); // Empty dependency array means this runs once on mount

  // This function is identical to Home.jsx, handling navigation
  const handleToolClick = (toolSlug) => {
    navigate(`/tools/${toolSlug}`);
  };

  // Loading state display, same as Home.jsx
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading Image Tools...</div>
      </div>
    );
  }

  // If the category or its tools couldn't be loaded
  if (!imageCategoryData || !imageCategoryData.tools || imageCategoryData.tools.length === 0) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white text-xl text-center">
          <p>Could not load image tools.</p>
          <p className="text-sm text-gray-400 mt-2">Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  // Dynamically get the translated display name for the category title
  const categoryDisplayName = imageCategoryData[`display_name_${lang}`] || imageCategoryData.display_name_en || 'Image Tools';

  return (
    <div className="min-h-screen bg-brand-dark text-gray-200">
      <div className="container mx-auto px-4 py-12 md:py-16">
        
        {/* Page Header */}
        <div className="text-left mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {categoryDisplayName}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            {/* Using the same translation key pattern as Home.jsx */}
            {t('image_tools_desc')}
          </p>
        </div>

        {/* Tools Grid - Simplified from Home.jsx since we don't need to loop through categories */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {imageCategoryData.tools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onToolClick={handleToolClick}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default ImageTools;